import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, AlertCircle, FileText } from 'lucide-react';
import { hypedditParserService } from '../../../../backend/services/hypedditParser';

interface HypedditPrevalidation {
  source: 'hypeddit';
  file: File;
  fileName?: string;
  fileSize?: number;
  valid: boolean;
  errors: string[];
  warnings: string[];
  parsed?: any;
  headers?: string[];
  rows?: Array<Record<string, any>>;
  format?: 'campaigns' | 'smartlinks' | 'combined';
}

interface Props {
  onPrevalidated: (result: HypedditPrevalidation) => void;
  compact?: boolean;
}

export const HypedditSourceCard: React.FC<Props> = ({ onPrevalidated, compact = false }) => {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<HypedditPrevalidation | null>(null);

  const onDrop = useCallback(async (accepted: File[]) => {
    if (!accepted.length) return;
    const file = accepted[0];
    setBusy(true);
    try {
      const html = await file.text();
      const validation = hypedditParserService.validateHypedditHTML(html);
      let parsed: any | undefined;
      if (validation.valid) {
        parsed = await hypedditParserService.parseHypedditHTML(html, file.name);
      }
      const format: any = parsed ? (parsed.campaigns?.length && parsed.smartlinks?.length ? 'combined' : parsed.campaigns?.length ? 'campaigns' : 'smartlinks') : undefined;
      const result: HypedditPrevalidation = {
        source: 'hypeddit',
        file,
        fileName: file.name,
        fileSize: file.size,
        valid: validation.valid,
        errors: validation.errors,
        warnings: validation.warnings,
        parsed,
        format
      };
      setLast(result);
      onPrevalidated(result);
    } finally {
      setBusy(false);
    }
  }, [onPrevalidated]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: { 'text/html': ['.html', '.htm'] },
    maxSize: 50 * 1024 * 1024,
    multiple: false
  });

  return (
    <div className={`${compact ? 'p-2' : 'p-4'} rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900`}>
      {!compact && (
        <div className="mb-3 flex items-center gap-2">
          <img src="/brand/hypeddit_logo.png" alt="Hypeddit" className="w-5 h-5" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Hypeddit [HTML]</span>
        </div>
      )}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg ${compact ? 'p-2' : 'p-6'} text-center cursor-pointer transition-colors ${
          isDragActive && !isDragReject
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : isDragReject
            ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <input {...getInputProps()} />
        <div className={`${compact ? 'mb-1' : 'mb-2'} flex justify-center`}>
          {isDragReject ? (
            <AlertCircle className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} text-red-500`} />
          ) : (
            <Upload className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} text-gray-400`} />
          )}
        </div>
        <div className={`${compact ? 'text-xs' : 'text-sm'} text-gray-700 dark:text-gray-300`}>
          {busy ? 'Reading file…' : isDragActive ? (isDragReject ? 'Invalid file type' : 'Drop files here') : 'Drag & drop or click to browse'}
        </div>
      </div>

      {!compact && last && (
        <div className="mt-3 text-xs">
          <div className="flex items-center gap-2">
            <FileText className="w-3 h-3 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">{last.file.name}</span>
          </div>
        </div>
      )}
    </div>
  );
};

